import { useState } from 'react'
import type { FormEvent } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '../../lib/utils'
import { createEmptySecretDraft } from './secret-dashboard-utils'
import type { SecretDraft, SecretKind } from './secret-dashboard-types'

interface SecretCreatePanelProps {
  environment: string
  path: string
  isDark: boolean
  isPending: boolean
  onCreate: (draft: SecretDraft) => Promise<void>
}

function inputClass(isDark: boolean) {
  return cn(
    'rounded-md border px-3 py-2 text-base outline-none md:text-sm',
    isDark ? 'border-white/[0.08] bg-surface-2 text-neutral-100 placeholder-neutral-600' : 'border-neutral-200 bg-white text-neutral-900 placeholder-neutral-400',
  )
}

export function SecretCreatePanel({ environment, path, isDark, isPending, onCreate }: SecretCreatePanelProps) {
  const [draft, setDraft] = useState<SecretDraft>(createEmptySecretDraft())

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    await onCreate(draft)
    setDraft(createEmptySecretDraft())
  }

  return (
    <form onSubmit={handleSubmit} className={cn('grid gap-3 border-b pb-4 md:grid-cols-[1fr_1fr_120px_1fr_1fr_auto]', isDark ? 'border-white/[0.06]' : 'border-neutral-200')}>
      <input className={inputClass(isDark)} value={draft.key} onChange={(e) => setDraft((s) => ({ ...s, key: e.target.value }))} placeholder="KEY_NAME" required />
      <input className={inputClass(isDark)} value={draft.value} onChange={(e) => setDraft((s) => ({ ...s, value: e.target.value }))} placeholder="Value" required />
      <select className={inputClass(isDark)} value={draft.kind} onChange={(e) => setDraft((s) => ({ ...s, kind: e.target.value as SecretKind }))}>
        <option value="secret">secret</option>
        <option value="value">value</option>
      </select>
      <input className={inputClass(isDark)} value={draft.description} onChange={(e) => setDraft((s) => ({ ...s, description: e.target.value }))} placeholder="Description" />
      <input className={inputClass(isDark)} value={draft.reason} onChange={(e) => setDraft((s) => ({ ...s, reason: e.target.value }))} placeholder="Reason" />
      <button disabled={isPending || !environment || !path} className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-500 disabled:opacity-50">
        <Plus className="h-4 w-4" /> {isPending ? 'Adding...' : 'Add'}
      </button>
    </form>
  )
}
